import { useState } from "react";
import { AlertOctagon, AlertTriangle, CheckCircle2, Upload } from "lucide-react";
import { Badge } from "@/components/atoms/studio/ui/badge";
import { Button } from "@/components/atoms/studio/ui/button";
import { Input } from "@/components/atoms/studio/ui/input";
import { EngineClearApplyDialog } from "./EngineClearApplyDialog";
import { EngineVerificationPanel } from "./EngineVerificationPanel";
import { useEngineApply } from "@/hooks/migrations/engine";
import {
  abandonedNotice,
  applyBlockers,
  clearApplyUnavailableReason,
  parkedApplyAttempt,
} from "@/lib/migrations/apply";
import { engineErrorMessage, engineTaskPath } from "@/services/migrations/engine";
import type { EngineApplyAttempt, EngineTaskReport } from "@/services/migrations/engine";

/**
 * The write path, for one task.
 *
 * WHAT THIS SCREEN IS FOR. The engine stops at a validated, check-only artifact. Getting
 * that artifact into a customer's org is a separate act with five independent factors,
 * every one of which is checked server-side on every request: the apply flag, an explicit
 * `op`, `check_only` as the literal boolean false, a verbatim token naming (org,
 * component, compile key), and a recorded GATE_APPLY approval whose compile key equals
 * this artifact's. Nothing here can weaken any of them.
 *
 * WHAT IT ADDS ON TOP OF THEM, WHICH IS THE POINT. The server can refuse a bad write. It
 * cannot refuse an uninformed one — an approval given without reading anything passes
 * every check it makes. So this panel states, before it offers the field:
 *
 *   * WHAT WILL BE CREATED — the exact metadata type and component name, from the
 *     server's `will_create`, not from a filename this screen guessed;
 *   * WHETHER THE RECORDED APPROVAL STILL NAMES THIS ARTIFACT — `compile_key` moves
 *     whenever any input to the artifact moves, so an approval given for an earlier
 *     compile does not transfer, and the ledger row still reads "approved by <name>"
 *     either way. That comparison is made server-side and printed here;
 *   * EVERY ATTEMPT ALREADY MADE, including the ones that did not settle.
 *
 * And the token is not pre-filled. It is displayed — it is not a secret, and the backend
 * renders it beside the decision for exactly this reason — but it must be typed, because
 * the act of repeating a sentence that names the org and the component is the difference
 * between a deliberate write and a click.
 */
export function EngineApplyPanel({
  planId,
  task,
  orgId,
}: {
  planId: string;
  task: EngineTaskReport;
  orgId?: string | null;
}) {
  const apply = task.apply;
  const taskPath = engineTaskPath(task);
  const [typed, setTyped] = useState("");
  const [clearing, setClearing] = useState(false);
  const mutation = useEngineApply(planId, taskPath);

  const blockers = applyBlockers(task, typed);
  const parked = parkedApplyAttempt(task);
  const parkedReason = clearApplyUnavailableReason(task);
  const result = mutation.data;

  // Nothing has ever been proposed for this task and nothing was ever sent: an apply
  // section here would be a control panel for a machine that does not exist.
  if (!apply.enabled && apply.attempts.length === 0 && !apply.wrote_to_org) {
    return (
      <p className="max-w-prose font-mono text-[11px] text-muted-foreground">
        The write path is off in this environment. This plan compiles and validates against the
        org; it cannot change it.
      </p>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-mono text-[11px] uppercase tracking-wide text-muted-foreground">
          apply to org
        </span>
        {apply.wrote_to_org ? (
          <Badge variant="warm" title="A component of this task exists in the org because of this plan.">
            written to the org
          </Badge>
        ) : (
          <Badge variant="secondary">nothing written</Badge>
        )}
        {apply.approved ? (
          <Badge variant={apply.approval_matches_artifact ? "secondary" : "destructive"}>
            {apply.approval_matches_artifact ? "approved" : "approval stale"}
          </Badge>
        ) : (
          <Badge variant="secondary">not approved</Badge>
        )}
      </div>

      <WillCreate task={task} />

      {apply.approved ? <ApprovalBinding task={task} /> : null}

      {/* ── the parked state and its only door ─────────────────────────────── */}
      {parked ? (
        <div className="space-y-2 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-xs text-destructive">
          <div className="flex items-start gap-2">
            <AlertOctagon className="mt-0.5 h-4 w-4 shrink-0" />
            <span className="min-w-0 flex-1 leading-relaxed">
              <span className="font-semibold">An apply attempt on this task never settled.</span>{" "}
              The engine could not establish whether the deploy reached the org, so the task is
              parked and nothing will re-submit. Whether the org changed is exactly what is not
              known — go and look in the org before closing this.
            </span>
          </div>
          <Button variant="destructive" size="sm" onClick={() => setClearing(true)}>
            Close this attempt by hand
          </Button>
        </div>
      ) : parkedReason ? (
        <p className="max-w-prose rounded-md border border-destructive/40 bg-destructive/10 p-3 text-xs text-destructive">
          {parkedReason}
        </p>
      ) : null}

      {clearing && parked ? (
        <EngineClearApplyDialog
          planId={planId}
          task={task}
          attempt={parked}
          orgId={orgId}
          onClose={() => setClearing(false)}
        />
      ) : null}

      <AttemptHistory attempts={apply.attempts} />

      {/* ── the write ──────────────────────────────────────────────────────── */}
      {apply.confirmation_token ? (
        <div className="space-y-2">
          <p className="max-w-prose text-xs text-muted-foreground">
            To create this component in the org, type the sentence below exactly. It names the
            org, the component and the full compile key, so it stops matching the instant this
            artifact recompiles — at the same instant the approval displayed beside it stops
            being valid.
          </p>
          <p className="break-all rounded border border-border bg-muted/30 p-2 font-mono text-[11px] text-foreground">
            {apply.confirmation_token}
          </p>
          <Input
            value={typed}
            onChange={(event) => setTyped(event.target.value)}
            placeholder="Type the confirmation token"
            disabled={mutation.isPending}
            className="max-w-2xl font-mono text-xs"
            spellCheck={false}
            autoComplete="off"
          />
        </div>
      ) : null}

      {blockers.length > 0 ? (
        <ul className="space-y-1">
          {blockers.map((blocker) => (
            <li key={blocker.code} className="flex items-start gap-2 text-xs text-muted-foreground">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[hsl(var(--accent-warm))]" />
              <span className="min-w-0 flex-1 leading-relaxed">
                <span className="font-mono uppercase">{blocker.code}</span> — {blocker.message}
              </span>
            </li>
          ))}
        </ul>
      ) : null}

      <div className="flex flex-wrap items-center gap-2">
        <Button
          variant="destructive"
          size="sm"
          disabled={blockers.length > 0 || mutation.isPending}
          onClick={() => mutation.mutate(typed)}
        >
          <Upload className="mr-1 h-3.5 w-3.5" />
          {mutation.isPending ? "Applying…" : "Create in the org"}
        </Button>
        <span className="font-mono text-[10px] uppercase tracking-wide text-muted-foreground">
          this creates metadata in a real org
        </span>
      </div>

      {mutation.isError ? (
        <p className="font-mono text-xs text-destructive">
          The apply request itself failed: {engineErrorMessage(mutation.error)}. Nothing can be
          concluded about the org from a request that did not complete — reload the report.
        </p>
      ) : null}

      {result ? <ApplyResultBanner result={result} /> : null}
    </div>
  );
}

/** Exactly what a real apply would create. This phase modifies and deletes nothing. */
function WillCreate({ task }: { task: EngineTaskReport }) {
  const { will_create } = task.apply;
  if (will_create.length === 0) {
    return (
      <p className="font-mono text-xs text-muted-foreground">
        This task has no artifact, so there is nothing to create.
      </p>
    );
  }
  return (
    <div className="space-y-1">
      <span className="font-mono text-[11px] uppercase tracking-wide text-muted-foreground">
        will create
      </span>
      <ul className="space-y-0.5">
        {will_create.map((item) => (
          <li
            key={`${item.metadata_type}-${item.component_key}`}
            className="break-all font-mono text-xs text-foreground"
          >
            {item.metadata_type} · {item.component_key}
          </li>
        ))}
      </ul>
      <p className="max-w-prose text-[11px] text-muted-foreground">
        Create only. Nothing is modified and nothing is deleted, and the source (the Profile or
        WorkflowRule this was compiled from) is not a type this engine may write at all.
      </p>
    </div>
  );
}

/**
 * Whether the recorded approval still names THIS artifact.
 *
 * The failure this exists to prevent is invisible by eye: the ledger row reads "approved
 * by <name>" whether or not the artifact has recompiled since. Only the keys differ.
 */
function ApprovalBinding({ task }: { task: EngineTaskReport }) {
  const { approved_compile_key, approval_matches_artifact } = task.apply;
  return (
    <div
      className={[
        "space-y-1 rounded-md border p-3 text-xs",
        approval_matches_artifact
          ? "border-border bg-muted/20 text-muted-foreground"
          : "border-destructive/40 bg-destructive/10 text-destructive",
      ].join(" ")}
    >
      <div className="flex items-center gap-2">
        {approval_matches_artifact ? (
          <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
        ) : (
          <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
        )}
        <span className="font-semibold">
          {approval_matches_artifact
            ? "The recorded approval names this artifact."
            : "The recorded approval names a DIFFERENT artifact."}
        </span>
      </div>
      <p className="break-all font-mono text-[10px]">approved {approved_compile_key || "—"}</p>
      <p className="break-all font-mono text-[10px]">artifact {task.compile_key || "—"}</p>
      {approval_matches_artifact ? null : (
        <p className="max-w-prose leading-relaxed">
          The compile key moves whenever any input to the artifact moves, so this artifact was
          recompiled after it was approved and the approval does not transfer. Re-approve the
          current one.
        </p>
      )}
    </div>
  );
}

function AttemptHistory({ attempts }: { attempts: EngineApplyAttempt[] }) {
  if (attempts.length === 0) return null;
  return (
    <div className="space-y-1">
      <span className="font-mono text-[11px] uppercase tracking-wide text-muted-foreground">
        attempts ({attempts.length})
      </span>
      <ul className="divide-y divide-border">
        {attempts.map((attempt, index) => {
          const notice = abandonedNotice(attempt.state);
          return (
            <li key={`${attempt.kind}-${index}`} className="space-y-0.5 py-1.5">
              <div className="flex flex-wrap items-baseline gap-x-2 font-mono text-[11px]">
                <span className="text-foreground">{attempt.kind}</span>
                <span
                  className={
                    attempt.state === "open" ? "text-destructive" : "text-muted-foreground"
                  }
                >
                  {attempt.state}
                </span>
                {attempt.deploy_id ? (
                  <span className="text-muted-foreground/70">deploy {attempt.deploy_id}</span>
                ) : null}
                {attempt.rolled_back_by ? (
                  <span className="text-[hsl(var(--accent-warm))]">rolled back</span>
                ) : null}
                {attempt.requested_at ? (
                  <span className="text-muted-foreground/70">{attempt.requested_at}</span>
                ) : null}
              </div>
              {notice ? (
                <p className="max-w-prose text-[11px] leading-relaxed text-destructive">{notice}</p>
              ) : null}
              {/* THE READ-BACK, beside the attempt that produced it. Rendered for every
                  attempt including the ones with nothing to show, because "no read-back is
                  recorded" and "it matched" are the two states this list must never merge. */}
              <EngineVerificationPanel attempt={attempt} />
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/**
 * The outcome of an apply. `sent` leads, because it is the fact everything else is read
 * in the light of — and it is a field on the response rather than something inferred
 * from a status code, since every one of these arrives as a 200.
 */
function ApplyResultBanner({
  result,
}: {
  result: { outcome: string; sent: boolean; code?: string; message?: string; expected?: string };
}) {
  const refused = !result.sent;
  return (
    <div
      className={[
        "space-y-1 rounded-md border p-3 text-xs",
        refused
          ? "border-border bg-muted/20 text-muted-foreground"
          : "border-[hsl(var(--accent-warm))]/45 bg-accent-warm-soft text-accent-warm",
      ].join(" ")}
    >
      <p className="font-semibold">
        {refused ? "Nothing was sent to the org." : "This request reached the org."}
      </p>
      <p className="font-mono text-[11px] uppercase">
        {result.outcome}
        {result.code ? ` · ${result.code}` : ""}
      </p>
      {result.message ? <p className="max-w-prose leading-relaxed">{result.message}</p> : null}
      {result.expected ? (
        <p className="break-all font-mono text-[10px]">expected token: {result.expected}</p>
      ) : null}
    </div>
  );
}
